<template>
  <div class="modal">
    <div class="modal__overlay" @click="closePopup"></div>
    <div class="modal__bg">
      <div class="modal__body">
        <div class="modal__title">
          User Details
        </div>
        <icon-base class="modal__close" @click.native="closePopup" red>
          <icon-cross/>
        </icon-base>
        <div class="modal__body-wrap">
          <spinner-el v-if="showSpinner" />
          <template v-else>
            <div
              v-if="userWasDeletedAt"
              class="sub-title sub-title_red"
            >User was removed at <span :title="userWasDeletedAt | format(dateTimeFormat)">{{userWasDeletedAt | format(dateFormat)}}</span></div>
            <div class="sub-title">
              Common
            </div>
            <table-el gray>
              <tr class="tr" slot="thead">
                <th class="td">Email</th>
                <th class="td">Phone</th>
                <th class="td">Timezone</th>
              </tr>
              <tr class="tr" slot="tbody">
                <td class="td td_email" :title="userInfo.settings">{{userInfo.settings.email || '—'}}</td>
                <td class="td">{{userInfo.settings.phone || '—'}}</td>
                <td class="td">{{userInfo.settings.timezone}}</td>
              </tr>
            </table-el>
            <div class="sub-title">
              Quotas
            </div>
            <table-el gray>
              <tr class="tr" slot="thead">
                <th class="td">SMS</th>
                <th class="td">Email</th>
                <th class="td">Push</th>
                <th class="td">Webhook</th>
              </tr>
              <tr class="tr" slot="tbody">
                <td class="td">{{`${userInfo.quotas.balance.sms}/${userInfo.quotas.limits.sms}`}}</td>
                <td class="td">{{`${userInfo.quotas.balance.email}/${userInfo.quotas.limits.email}`}}</td>
                <td class="td">{{`${userInfo.quotas.balance.push}/${userInfo.quotas.limits.push}`}}</td>
                <td class="td">{{`${userInfo.quotas.balance.webhook}/${userInfo.quotas.limits.webhook}`}}</td>
              </tr>
            </table-el>
            <div class="sub-title">
              Tariff
            </div>
            <table-el gray>
              <tr class="tr" slot="thead">
                <th class="td">Id</th>
                <th class="td">Name</th>
                <th class="td">Cost</th>
                <th class="td">Type</th>
                <th class="td">Active</th>
                <th class="td">Created At</th>
              </tr>
              <tr class="tr" slot="tbody">
                <td class="td">{{userInfo.tariffPlan.id}}</td>
                <td class="td">{{userInfo.tariffPlan.nickname}}</td>
                <td class="td">{{userInfo.tariffPlan.cost}}</td>
                <td class="td">{{userInfo.tariffPlan.type}}</td>
                <td class="td"><boolean-el :val="userInfo.tariffPlan.active"/></td>
                <td class="td">{{userInfo.tariffPlan.createdAt | format(dateFormat)}}</td>
              </tr>
            </table-el>
            <div v-if="!userInfo.notificationsHistory.length" class="sub-title">
              There aren't any Notifications.
            </div>
            <template v-else>
              <div class="sub-title">
                Notifications
              </div>
              <table-el gray>
                <tr class="tr" slot="thead">
                  <th class="td">Id</th>
                  <th class="td">Alert ID</th>
                  <th class="td">Message</th>
                  <th class="td">Created At</th>
                </tr>
                <tr v-for="notification in userInfo.notificationsHistory" :key="notification.id" class="tr" slot="tbody">
                  <td class="td">{{notification.id}}</td>
                  <td class="td">{{notification.alertId}}</td>
                  <td class="td">{{notification.message}}</td>
                  <td class="td">{{notification.createdAt | format(dateFormat)}}</td>
                </tr>
              </table-el>
            </template>
            <div v-if="!userInfo.paymentHistory.length" class="sub-title">
              There aren't any Payments.
            </div>
            <template v-else>
              <div class="sub-title">
                Payments
              </div>
              <table-el gray>
                <tr class="tr" slot="thead">
                  <th class="td">Card</th>
                  <th class="td">Payment Date</th>
                  <th class="td">Period</th>
                  <th class="td">Amount</th>
                </tr>
                <tr v-for="payment in userInfo.paymentHistory" :key="payment.paymentDate" class="tr" slot="tbody">
                  <td class="td">**** **** **** {{payment.lastDigits}}({{payment.cardBrand}})</td>
                  <td class="td">{{payment.paymentDate | format(dateFormat)}}</td>
                  <td class="td">{{payment.periodStart | format(dateFormat)}} — {{payment.periodEnd | format(dateFormat)}}</td>
                  <td class="td">{{payment.subtotal}} + {{payment.tax}} = {{payment.total}}</td>
                </tr>
              </table-el>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { dateFormat, dateTimeFormat } from 'src/utils/config';
import { IconCross } from 'Icons';
import booleanEl from './BooleanEl';

export default {
  components: {
    IconCross,
    booleanEl,
  },
  props: {
    userWasDeletedAt: String,
    userId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    showSpinner: true,
    userInfo: null,
  }),
  filters: {
    format,
  },
  computed: {
    dateFormat: () => dateFormat,
    dateTimeFormat: () => dateTimeFormat,
  },
  mounted() {
    this.$apiService.getUserDetails(this.userId).then((data) => {
      this.userInfo = data;
      this.showSpinner = false;
    });
  },
  methods: {
    closePopup() {
      this.$emit('closePopup');
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.modal {
  $side-padding: 30px;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 10000;
  &__overlay {
    width: 100%;
    height: 100%;
    background: $black-color5;
    z-index: 1;
    position: relative;
  }
  &__bg {
    overflow-y: auto;
    width: 100%;
    height: 100%;
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
  }
  &__title {
    @include font(24px, $black-color1, 500);
    border-bottom: 1px solid $white-color5;
    padding: 33px $side-padding 31px;
    margin-bottom: 37px;
  }
  &__close {
    cursor: pointer;
    position: absolute;
    right: 41px;
    top: 33px;
  }
  &__body {
    border-radius: 5px;
    margin: auto;
    background: $white-color2;
    position: relative;
    top: 60px;
    margin-bottom: 60px;
    max-width: 713px;
    width: 100%;
    z-index: 2;
  }
  &__body-wrap {
    padding: 0 $side-padding;
  }
}
.spinner-container {
  display: flex;
  justify-content: center;
}
.spinner {
  margin: auto;
}
.td {
  text-align: left;
  padding-left: 20px;
  padding-right: 20px;
}
.table {
  width: 100%;
}
.sub-title {
  font-size: 20px;
  margin-bottom: 20px;
  margin-top: 30px;
  &_red {
    color: $red-color3;
  }
}
@media screen and (max-width: 840px) {
  .modal__body {
    min-width: max-content;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/pages/admin/UserDetailsModal.vue